// Libs
import classNames from 'classnames/bind';
import { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseCheckbox, BaseDatePicker, BaseInput, BaseLabel, BaseSelect, Loading } from '~/components/common';
// Others
import { defaultValuesCouponForm, schema } from './helper';
import { IBaseOption, IBodyUpdate, IResultCallApiFn } from '~/utils/interface/common';
import { commonOptionStatus, EMPTY_STRING, optionsCommonUnit } from '~/utils/constants/common';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IFormCoupon, IFormUpdateCoupon } from '~/utils/interface/coupon';
import { compareDataUpdate } from '~/utils/helper/common';
import { createCoupon, getDetailCoupon, updateCoupon } from '~/thunks/coupon/couponThunk';
// Styles, images, icons
import styles from './CouponForm.module.scss';
import { icons } from '~/assets';

type Props = {
  idCoupon?: string;
  onClose: () => void;
  onRemoveFile?: (id: string) => void;
};

const cx = classNames.bind(styles);

const CouponFormModal = (props: Props) => {
  //#region Destructuring Props
  const { idCoupon, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/coupon', 'common/base']);
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<IFormCoupon>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesCouponForm,
  });
  //#endregion Declare Hook

  //#region Selector
  const { isLoading } = useAppSelector((state) => state.coupon);
  //#endregion Selector

  //#region Declare State
  const [initialData, setInitialData] = useState<IFormCoupon>(defaultValuesCouponForm);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!idCoupon) return;

    handleGetDetailCoupon(idCoupon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCoupon]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseModal = () => {
    onClose && onClose();
    reset();
  };

  const handleFormCoupon = (data: IFormCoupon) => {
    if (idCoupon) {
      handleEditCoupon(data);
    } else {
      handleAddCoupon(data);
    }
  };

  const handleAddCoupon = async (data: IFormCoupon) => {
    dispatch(createCoupon(data))
      .unwrap()
      .then((_res) => {
        handleCloseModal();
      })
      .catch((_error) => {})
      .finally(() => {});
  };

  const handleEditCoupon = async (data: IFormUpdateCoupon) => {
    const dataChange = compareDataUpdate(initialData, data) as IFormUpdateCoupon;

    const params: IBodyUpdate<IFormUpdateCoupon> = {
      id: idCoupon || EMPTY_STRING,
      body: dataChange,
    };

    dispatch(updateCoupon(params))
      .unwrap()
      .then(() => {
        handleCloseModal();
      })
      .catch(() => {})
      .finally(() => {});
  };

  const fetchOptionsCouponType = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionsCommonUnit,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionsCouponStatus = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: commonOptionStatus,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handleGetDetailCoupon = (idCoupon: string) => {
    if (!idCoupon) return;

    dispatch(getDetailCoupon(idCoupon))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { id, ...remainingData } = res?.data;

        const body: IFormCoupon = {
          ...remainingData,
          name: remainingData?.name || EMPTY_STRING,
          discountRate: remainingData?.discountRate,
          discountType: remainingData?.discountType || EMPTY_STRING,
          startDate: remainingData?.startDate || EMPTY_STRING,
          endDate: remainingData?.endDate || EMPTY_STRING,
          quantity: remainingData?.quantity,
          isNoExpiration: remainingData?.isNoExpiration || false,
        };

        setInitialData(body);

        reset(body);
      })
      .catch((_error) => {})
      .finally(() => {});
  };
  //#endregion Handle Function

  return (
    <Dialog
      id='CouponFormModalComponent'
      aria-labelledby='CouponFormModal'
      open={true}
      onClose={handleCloseModal}
      fullWidth
      maxWidth={'md'}
    >
      <DialogTitle id='trailerFormModal' className={cx('header')}>
        <Stack direction={'column'} spacing={'10px'}>
          <Stack direction={'row'} spacing={10}>
            <BaseLabel
              label={t(idCoupon ? 'form_add_coupon_title_edit' : 'form_add_coupon_title')}
              styleType='bigTitle'
            />
          </Stack>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <Grid2 columnSpacing={'24px'} rowSpacing={'24px'} container>
          <Grid2 size={6}>
            <Controller
              control={control}
              name={'name'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('form_add_coupon_field_code')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.name?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'quantity'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('form_add_coupon_field_quantity')}
                    value={String(value)}
                    onChange={onChange}
                    errorMessage={errors?.quantity?.message}
                    type={'number'}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'discountRate'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('form_add_coupon_field_value')}
                    value={String(value)}
                    onChange={onChange}
                    errorMessage={errors?.discountRate?.message}
                    required
                    type={'number'}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'discountType'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseSelect
                    gap={10}
                    height={36}
                    styleType='contained'
                    label={t('form_add_coupon_field_unit')}
                    placeholder={t('common_text_placeholder_select', { ns: 'common/base' })}
                    callApiFn={fetchOptionsCouponType}
                    value={value}
                    onChange={(data) => onChange(data?.value)}
                    errorMessage={errors?.discountType?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'startDate'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseDatePicker
                    mode='date'
                    label={t('form_add_coupon_field_start_date')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors.startDate?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'endDate'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseDatePicker
                    mode='date'
                    label={t('form_add_coupon_field_end_date')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors.endDate?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              control={control}
              name={'isNoExpiration'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseCheckbox
                    label={t('form_add_coupon_field_no_expiration')}
                    checked={value || false}
                    onChange={(value) => onChange(value)}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'status'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseSelect
                    gap={10}
                    height={36}
                    styleType='contained'
                    label={t('form_add_coupon_field_status')}
                    placeholder={t('common_text_placeholder_select', { ns: 'common/base' })}
                    callApiFn={fetchOptionsCouponStatus}
                    value={value}
                    onChange={(data) => onChange(data?.value)}
                    errorMessage={errors?.status?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button
          variant='contained'
          className={cx('buttonSave')}
          onClick={handleSubmit(handleFormCoupon)}
          disabled={idCoupon ? !isDirty : false}
        >
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default CouponFormModal;
